import { parseServerTimestamp } from '@/utils/time'
import { formatCountdown, formatEventClock, formatEventDay, formatEventTime, secondsUntil } from '../time'
import type { RoomState, WebinarRoomSpeakerDto } from '../types'
import { presentersShown } from './presenters'
import { RoomLogo } from './RoomLogo'
import type { RoomLogos } from './RoomLogo'
import { SpeakerAvatar } from './SpeakerAvatar'
import type { SlateStage } from './stage'

export type SlateSpeaker = Pick<WebinarRoomSpeakerDto, 'Name' | 'Title' | 'Company' | 'PhotoUrl'>

/** Past this many the cards would run off the stage, so the last place says how many more there are. */
const PRESENTER_CARDS_MAX = 6

interface ViewerSlateProps {
    stage: SlateStage
    room: Pick<RoomState, 'Title' | 'StartUtc' | 'EndUtc' | 'EntryOpensUtc' | 'TimeZone' | 'ReplayAvailable'>
    logos: RoomLogos
    companyName: string
    speakers: SlateSpeaker[]
    /** Hybrid webs have a replay, so the closing slate says it is coming. */
    hybrid: boolean
    now: number
}

/**
 * Who is presenting, as cards under the countdown: one large card each for one or two presenters, smaller ones in
 * rows for three or more.
 */
function PresenterCards({ speakers }: { speakers: SlateSpeaker[] }) {
    const { shown, more } = presentersShown(speakers, PRESENTER_CARDS_MAX)
    return (
        <ul class={`slate-pres${speakers.length > 2 ? ' is-compact' : ''}`} aria-label="Presenting">
            {shown.map((speaker, index) => {
                const role = [speaker.Title, speaker.Company].filter(Boolean).join(', ')
                return (
                    <li class="slate-card" key={`${index}:${speaker.Name}`}>
                        <SpeakerAvatar name={speaker.Name} photoUrl={speaker.PhotoUrl ?? null} className="slate-photo" />
                        <span class="slate-card-text">
                            <span class="slate-card-name">{speaker.Name}</span>
                            {role && <span class="slate-card-role">{role}</span>}
                        </span>
                    </li>
                )
            })}
            {more > 0 && <li class="slate-more">+{more} more</li>}
        </ul>
    )
}

/**
 * The whole stage in the client's brand, whenever there is no video: before the room opens, the holding slate with
 * its countdown to the start and who is presenting, the Q&A after a video shorter than the slot, and the closing
 * slate. Nothing here sits over a picture, so it may fill the stage.
 */
export function ViewerSlate({ stage, room, logos, companyName, speakers, hybrid, now }: ViewerSlateProps) {
    const startMs = parseServerTimestamp(room.StartUtc)
    const endMs = parseServerTimestamp(room.EndUtc)
    const entryMs = parseServerTimestamp(room.EntryOpensUtc)

    let eyebrow: string
    let title = room.Title
    let count: number | null = null
    let when: string | null = null
    if (stage === 'ended') {
        eyebrow = 'Session ended'
        title = 'Thanks for joining'
        // The replay comes out a minute or so after the end, and this page plays it then without a reload.
        if (hybrid) when = room.ReplayAvailable ? 'The replay is ready.' : 'The replay will be here shortly.'
    } else if (stage === 'afterVideo') {
        eyebrow = 'Live Q&A'
        title = 'The presentation has finished'
        const until = endMs === null ? '' : ` until ${formatEventTime(new Date(endMs), room.TimeZone)}`
        when = `The presenters are taking questions${until}. Ask yours in the chat with “Ask as a question”.`
    } else {
        eyebrow = 'Starting soon'
        count = secondsUntil(startMs, now)
    }
    const before = stage === 'beforeEntry' || stage === 'holding'
    if (before && startMs !== null) {
        const start = new Date(startMs)
        // The count is always to the start, which a count to the doors was read as; the doors are only a note.
        const doors = stage === 'beforeEntry' && entryMs !== null ? ` · doors open ${formatEventClock(new Date(entryMs), room.TimeZone)}` : ''
        when = `${formatEventDay(start, room.TimeZone)} · ${formatEventTime(start, room.TimeZone)}${doors}`
    }
    const presenting = before && speakers.length > 0

    return (
        <div class="slate">
            <div class={presenting ? 'slate-inner has-pres' : 'slate-inner'}>
                <div class="slate-logo"><RoomLogo logos={logos} companyName={companyName} /></div>
                <p class="slate-eyebrow">{eyebrow}</p>
                {title && <h2 class="slate-title">{title}</h2>}
                {count !== null && <p class="slate-count" role="timer">{formatCountdown(count)}</p>}
                {when && <p class="slate-when">{when}</p>}
                {presenting && <PresenterCards speakers={speakers} />}
            </div>
        </div>
    )
}
